import {useExpandedState} from '@hconnect/common/hooks'
import {useUpmPlantContext} from '@hconnect/upm'
import {yupResolver} from '@hookform/resolvers/yup'
import {Box} from '@mui/material'
import {useCallback, useMemo, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {
  SlaKpiTabsValuesTableHeader,
  SlaKpiTabsValuesTable,
  SlaKpiTabsValuesTableProps
} from '../components'
import {useModifySlaKpiValues, useSelectedSlaKpi, useSlaKpiValuesValidationSchema} from '../hooks'
import {KpiTabValuesDto} from '../types'
import {getAllKpiValuesById, getAllVersionsByKpiValueId} from '../utils'

import {SlaKpiCommentsVersionsContainer} from './SlaKpiCommentsVersionsContainer'

export type SlaKpiTabsValuesTableContainerProps = {
  data: KpiTabValuesDto
  dataFieldsCount: number
  showDataFieldsCount: boolean
  isDesktop: boolean
  isSubmitButtonVisible: boolean
  cceKpiNameMap: Map<number, string>
} & Pick<SlaKpiTabsValuesTableProps, 'isCellEditable' | 'isCellCreatable'>

type SlaKpiTabsValuesTableContainerFormValues = {
  [key: string]: number | null
}

export const SlaKpiTabsValuesTableContainer = ({
  data,
  dataFieldsCount,
  showDataFieldsCount,
  isDesktop,
  isCellEditable,
  isCellCreatable,
  isSubmitButtonVisible,
  cceKpiNameMap
}: SlaKpiTabsValuesTableContainerProps) => {
  const {upmId: plantUpmId} = useUpmPlantContext()
  const isEditableOrCreatable = useCallback(
    (month: number, year: number, value: number | undefined) => {
      return isCellEditable(month, year) || (value === undefined && isCellCreatable(month, year))
    },
    [isCellCreatable, isCellEditable]
  )

  const kpiValuesById = useMemo(
    () => getAllKpiValuesById(data.kpiDefinitions, isEditableOrCreatable),
    [data, isEditableOrCreatable]
  )
  const kpiVersionsToValues = useMemo(
    () => getAllVersionsByKpiValueId(data.kpiDefinitions, isEditableOrCreatable),
    [data, isEditableOrCreatable]
  )
  const validationSchema = useSlaKpiValuesValidationSchema(kpiValuesById)
  const [isCommentsVersionsModalOpen, setIsCommentsVersionsModalOpen] = useState(false)
  const {selectedKpiValueId, setSelectedKpiValueId, selectedKpiDefinition} = useSelectedSlaKpi(data)

  const methods = useForm<SlaKpiTabsValuesTableContainerFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: kpiValuesById
  })
  const {
    reset: resetForm,
    formState: {isSubmitting, isValid, isDirty}
  } = methods

  const {mutateAsync: modifyKpiValues} = useModifySlaKpiValues({
    plantUpmId
  })

  const handleSubmit = useCallback(
    async (values: SlaKpiTabsValuesTableContainerFormValues) => {
      const validEntries = Object.entries(values)

      await modifyKpiValues({
        kpiValues: validEntries
          // Filter out not filled values values before submitting to API
          .filter(([id]) => kpiVersionsToValues[id] !== undefined)
          .map(([id, value]) => {
            const version = kpiVersionsToValues[id]

            return {
              id,
              value: value as number,
              version
            }
          })
      })
      resetForm(values)
    },
    [modifyKpiValues, kpiVersionsToValues, resetForm]
  )

  // deduplicate group ids
  const allExpandableGroupIds = useMemo(
    () => [...new Set(data.kpiGroups?.map((group) => group.id) ?? [])],
    [data.kpiGroups]
  )

  const {allExpanded, getIsExpanded, toggleExpandCollapse, toggleExpandCollapseAll} =
    useExpandedState(allExpandableGroupIds)

  const handleOpenCommentsVersions = useCallback(
    (kpiId: string) => {
      setSelectedKpiValueId(kpiId)
      setIsCommentsVersionsModalOpen(true)
    },
    [setSelectedKpiValueId]
  )

  return (
    <>
      <Box>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)} style={{width: '100%'}}>
            <SlaKpiTabsValuesTableHeader
              dataFieldsCount={dataFieldsCount}
              tabType={data.tabType}
              showDataFieldsCount={showDataFieldsCount}
              isSubmitButtonVisible={isSubmitButtonVisible}
              isDirty={isDirty}
              isSubmitting={isSubmitting}
              isValid={isValid}
              allExpanded={allExpanded}
              isExpandCollapseAllButtonVisible={allExpandableGroupIds.length > 0}
              toggleExpandCollapseAll={toggleExpandCollapseAll}
            />
            <SlaKpiTabsValuesTable
              data={data}
              getIsExpanded={getIsExpanded}
              toggleExpandCollapse={toggleExpandCollapse}
              isDesktop={isDesktop}
              tabType={data.tabType}
              isCellEditable={isCellEditable}
              isCellCreatable={isCellCreatable}
              onOpenCommentsVersions={handleOpenCommentsVersions}
              cceKpiNameMap={cceKpiNameMap}
            />
          </form>
        </FormProvider>
      </Box>
      {isCommentsVersionsModalOpen && selectedKpiDefinition && selectedKpiValueId && (
        <SlaKpiCommentsVersionsContainer
          isOpen={isCommentsVersionsModalOpen}
          onClose={() => setIsCommentsVersionsModalOpen(false)}
          kpiName={selectedKpiDefinition.name}
          kpiUnit={selectedKpiDefinition.slaTarget.unit}
          kpiValueId={selectedKpiValueId}
        />
      )}
    </>
  )
}
